/**
 * GTCC-BAU G1 — GENIUSTRACKER CONTROL COCKPIT shell skeleton.
 *
 * G1 shipped the shell skeleton: Mono-Deck nav (6 groups / initially 17 stub views),
 * topbar, content frame. No new API calls. Gated on
 * `GTCC_SHELL_ENABLED` (packages/config/src/gtcc-flags.ts) AND
 * platform-owner role — both checks must pass or the calm gate
 * renders and no shell state mounts.
 *
 * G2a adds the overlay nav / Ask Genius shell on top of the G1 frame:
 * the burger toggles the sidebar (a start-side Drawer below
 * `--gt-cmp-gtcc-nav-collapse-bp`, a show/hide toggle of the fixed
 * column above it), the Ask Genius button opens a read-only end-side
 * Drawer, and an optional `?gtcc=<viewKey>` query param selects the
 * initial view once on mount.
 *
 * I-GTCC-47 ergaenzt den build-base-faehigen Pfad `/gtcc/<viewKey>` als
 * kanonische Adresse. Bestehende `?gtcc=`-Bookmarks bleiben kompatibel;
 * Browser-Zurueck synchronisiert beide URL-Schemata (`deep-link.ts`).
 */
import { Card, Drawer, EmptyState, useTheme } from "@genius-tracker/design-system";
import { resolveGtccFlags } from "@genius-tracker/config";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useLayoutEffect, useState } from "react";

/**
 * D-GTCC-FRAME1 — some views (e.g. `GtccCockpitView`) keep their own tab
 * selection in the URL's `?tab=` param via `history.replaceState`, which
 * fires no `popstate` event. The Shell needs to notice those changes to
 * keep the breadcrumb's 3rd level in sync, so it patches `pushState`/
 * `replaceState` once (module scope — one patch no matter how many times
 * the Shell mounts) to also dispatch a `gtcc:locationchange` event.
 */
let historyPatchedForGtccBreadcrumb = false;
function ensureGtccLocationChangeEvents(): void {
  if (historyPatchedForGtccBreadcrumb || typeof window === "undefined") return;
  historyPatchedForGtccBreadcrumb = true;
  for (const method of ["pushState", "replaceState"] as const) {
    const original = window.history[method];
    window.history[method] = function patched(
      this: History,
      ...args: Parameters<History[typeof method]>
    ) {
      const result = original.apply(this, args);
      window.dispatchEvent(new Event("gtcc:locationchange"));
      return result;
    } as History[typeof method];
  }
}

import { usePlatformOwner } from "../access/platform-owner.js";
import { apiGet } from "../lib/api.js";
import { isCockpitEntryBundle } from "../lib/postLoginLanding.js";
import {
  isMailHealthResponse,
  MAIL_HEALTH_QUERY_KEY,
  mailHealthWarningCount,
} from "../mail/MailHealthPage.js";
import { useSession } from "../session/SessionProvider.js";
import { ALERTS_SUMMARY_KEY, fetchGtccAlertsSummary } from "./api/alerts.js";
import {
  isMaxWidthBreakpointNow,
  resolveMaxWidthBreakpointQuery,
  useBreakpointMatch,
} from "./breakpoint.js";
import { readGtccViewKeyFromLocation, writeGtccViewKeyToLocation } from "./deep-link.js";
import "./gtcc-shell.css";
import { GtccIcon, isGtccIconKey } from "./gtcc-icons.js";
import { GtccStubView } from "./GtccStubView.js";
import { GtccViewBoundary } from "./GtccViewBoundary.js";
import { GtccHeadTabsProvider } from "./views/control/HeadTabsContext.js";
import {
  GTCC_DEFAULT_VIEW_KEY,
  GTCC_GROUPS,
  GTCC_VIEWS,
  gtccChildrenOf,
  gtccViewByKey,
  type GtccPill,
  type GtccViewDef,
} from "./registry.js";
import { AskGeniusPanel } from "./AskGeniusPanel.js";
import { GalleryView } from "./ui/GalleryView.js";

/**
 * B-GTCC-UI1 — the primitives gallery. Deliberately NOT a `registry.ts`
 * entry: it carries no i18n/query/a11y contract of its own (it just
 * showcases the 1:1 primitives with fixed reference example data for the
 * A-GTCC-SHOT1 screenshot harness), so it is resolved directly from the
 * address bar here, before the normal `GTCC_VIEWS` lookup, and is never
 * offered as a `GtccNav` item. This keeps every `GTCC_VIEWS`-driven guard
 * test (registry.test.ts's "exactly one group" invariant, the a11y/i18n/
 * fetch-hygiene smoke suites, etc.) untouched.
 */
const GTCC_UI_GALLERY_KEY = "ui-gallery";

function isGtccUiGalleryLocation(): boolean {
  if (typeof window === "undefined") return false;
  const baseUrl = import.meta.env.BASE_URL;
  const base = !baseUrl || baseUrl === "/" ? "" : `/${baseUrl.replace(/^\/+|\/+$/g, "")}`;
  const prefix = `${base}/gtcc/`;
  const { pathname, search } = window.location;
  if (pathname.startsWith(prefix)) {
    const tail = pathname.slice(prefix.length).replace(/\/+$/, "");
    if (tail === GTCC_UI_GALLERY_KEY) return true;
  }
  return new URLSearchParams(search).get("gtcc") === GTCC_UI_GALLERY_KEY;
}

/** I-GTCC-47: Pfad und Legacy-Query werden in `deep-link.ts` aufgeloest. */
function initialViewKeyFromLocation(): string {
  if (isGtccUiGalleryLocation()) return GTCC_UI_GALLERY_KEY;
  return readGtccViewKeyFromLocation(import.meta.env.BASE_URL);
}

function useNavCollapsed(): boolean {
  return useBreakpointMatch(resolveMaxWidthBreakpointQuery);
}

export function GtccShell() {
  const { t } = useLocale();
  const session = useSession();
  const isOwner = usePlatformOwner(session);
  const cockpitBundle = isCockpitEntryBundle(import.meta.env);

  // D-GTCC-SHELL1 — the Cockpit bundle (gtcc.genius-tracker.com) always
  // mounts the shell; `GTCC_SHELL_ENABLED`/platform-owner stay a pre-shell
  // landing gate only for the `/admin/` bundle. Rights for a non-owner in
  // the Cockpit bundle are enforced server-side (403) and surfaced as an
  // in-shell notice (`GtccNoAccessNotice`), not by hiding the shell.
  if (!cockpitBundle) {
    const flags = resolveGtccFlags({ env: import.meta.env });
    const shellEnabled = flags.GTCC_SHELL_ENABLED.enabled;
    if (!shellEnabled || !isOwner) {
      return (
        <Card elevation={1} data-testid="gtcc-gate">
          <EmptyState title={t("admin.gtcc.title")} body={t("admin.gtcc.gate_body")} />
        </Card>
      );
    }
  }

  return <GtccShellBody isOwner={isOwner} />;
}

const GTCC_MAIN_ID = "gtcc-main";

/**
 * E-GTCC-29 — the content slot. A view that names a `component` in the
 * registry renders it; every other view keeps rendering `GtccStubView`.
 * No route is involved: the active view is state (`activeKey`), the same
 * way gtcc1 switches pages (`apps/admin/src/AdminApp.tsx:299`).
 */
function GtccActiveView({ view }: { readonly view: GtccViewDef }) {
  const ViewComponent = view.component;
  const body = ViewComponent ? <ViewComponent /> : <GtccStubView view={view} />;
  if (!view.danger) return body;
  return <GtccDangerFrame view={view}>{body}</GtccDangerFrame>;
}

/**
 * A-GTCC-DZ1 — die einheitliche Gefahrenzonen-Markierung am View-Kopf.
 * Sie sass bisher nur in `GtccStubView`, also nur an nicht ausgebauten
 * Views (Register Z45: TEIL). Hier im Inhaltsrahmen greift sie fuer
 * Stub UND ausgebaute View gleichermassen. Testid, `role="alert"` und
 * Text bleiben unveraendert, damit die vorhandenen Zusagen halten.
 */
function GtccDangerFrame({
  view,
  children,
}: {
  readonly view: GtccViewDef;
  readonly children: ReactNode;
}) {
  const { t } = useLocale();
  return (
    <div
      data-testid={`gtcc-view-${view.key}-danger-frame`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--gt-sys-space-3, 12px)",
        padding: "var(--gt-sys-space-5, 24px)",
        border: "1px solid var(--gt-sys-danger, #b91c1c)",
        borderRadius: "var(--gt-sys-radius-sm, 6px)",
      }}
    >
      <p
        role="alert"
        data-testid={`gtcc-view-${view.key}-danger-banner`}
        style={{
          margin: 0,
          fontSize: "var(--gt-sys-font-size-xs, 11px)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--gt-sys-danger, #b91c1c)",
        }}
      >
        {t("admin.gtcc.danger_zone_banner")}
      </p>
      {children}
    </div>
  );
}

/**
 * D-GTCC-SHELL1 — item 1: the Cockpit-root bundle mounts the full shell for
 * every signed-in user; a non-owner sees this notice in the content slot
 * instead of the gate `<Card>` used pre-shell. Server-side checks (403) stay
 * the actual enforcement — this is UX only, styled with the Cockpit tokens.
 */
function GtccNoAccessNotice() {
  const { t } = useLocale();
  return (
    <div
      role="alert"
      data-testid="gtcc-no-access-notice"
      style={{
        margin: "var(--gt-sys-space-5, 24px)",
        padding: "var(--gt-sys-space-5, 24px)",
        border: "1px solid var(--line, var(--gt-sys-border, #d1d5db))",
        borderRadius: "var(--radius, 10px)",
        background: "var(--panel, var(--gt-sys-surface, #fff))",
        color: "var(--tx, var(--gt-sys-text, #111827))",
      }}
    >
      <h2 style={{ fontFamily: "Sora, sans-serif", fontSize: "16px", margin: 0 }}>
        {t("admin.gtcc.no_access_title")}
      </h2>
      <p style={{ color: "var(--tx2, var(--gt-sys-text-muted, #6b7280))" }}>
        {t("admin.gtcc.no_access_body")}
      </p>
    </div>
  );
}

/**
 * A-GTCC-DZ1 — nur fuer Screenreader, gleiche Rezeptur wie die Shell-H1.
 */
const GTCC_VISUALLY_HIDDEN: CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

// D-GTCC-FRAME1 (A6) — views that run their own page-internal `?tab=` UI
// and must not also get the `parentKey`/`gtccChildrenOf` nav-level tab bar.
const GTCC_INTERNAL_TAB_VIEWS = new Set(["ten", "dplane"]);

// D-GTCC-ACCEPT-SHELL1: `aia` and `diag` fold `parentKey`-linked siblings
// that render the exact same component as the parent (P18/P19 fold) out of
// the nav-level tab bar — duplicating the page title as a second tab row
// achieves nothing (`aiwb`/`gicov`/`aihealth` under `aia`, `smoke` under
// `diag`). Siblings with a genuinely different component stay in the tab
// bar: it is their only in-app navigation path, since `parentKey` views
// never appear in the sidebar (e.g. `ppl` -> `GtccPeopleView` under `aia`,
// placement per GTCC_REFERENCE_SPEC.md §7). `mail` and `pset` are not in
// this set: none of their children share the parent's component, so folding
// would do nothing — their (unfoldable) tab bar instead moves under the
// heading via `GTCC_HEAD_TABS_VIEWS` below.
const GTCC_FOLDED_TAB_VIEWS = new Set(["aia", "diag"]);

// D-GTCC-VIEW-FIX-PSET-NOTI1 (pset 1), extended by D-GTCC-SHELL-TABSTRIP-POS1
// (`aia`, `mail`) — the reference never stacks a tab bar above a view's
// heading (`GTCC_REFERENCE_SPEC.md`: `cockpit` is the only view with a main
// tab bar, and even there the tabs render after the heading, see
// `GtccCockpitView.tsx`; confirmed against `docs/design/gtcc-screens/_reference/`
// for all three views below, which show no tab strip at all). None of
// `pset`/`aia`/`mail`'s children can simply fold away like
// `GTCC_FOLDED_TAB_VIEWS` does (`dom`/`cat` under `pset`, `ppl` under `aia`,
// `tg`/`edel` under `mail` are genuinely different components with no
// sidebar entry of their own — the tab bar is their only navigation path),
// so instead of stacking it above `GtccActiveView` the Shell hands it to the
// view's own `ControlHead` via `GtccHeadTabsContext`, which renders it after
// the heading (`AiaView.tsx`, `MailView.tsx`, `PsetView.tsx` all use
// `ControlHead`). `diag`'s only `parentKey` child (`smoke`) shares its
// component and is already folded away by `GTCC_FOLDED_TAB_VIEWS`, leaving
// zero tabs — it needs no entry here. `ten`/`dplane` render their own
// page-internal tab UI under the title already (`GTCC_INTERNAL_TAB_VIEWS`)
// and likewise need no entry here.
const GTCC_HEAD_TABS_VIEWS = new Set(["pset", "aia", "mail"]);

function GtccShellBody({ isOwner }: { readonly isOwner: boolean }) {
  const { t } = useLocale();
  const { resolved } = useTheme();
  const [activeKey, setActiveKey] = useState<string>(initialViewKeyFromLocation);
  const activeView = gtccViewByKey(activeKey) ?? gtccViewByKey(GTCC_DEFAULT_VIEW_KEY)!;
  // D-GTCC-SHELL1 — a view consolidated under a Referenz-Elternteil (`parentKey`)
  // renders in the content slot exactly as before, but the sidebar highlights its
  // parent and a tab bar (parent + `gtccChildrenOf`) lets the user switch siblings.
  // Deep-links to the old, now-nested key resolve here without a URL rewrite: the
  // key still selects the right content, and the parent's nav item goes active.
  const navActiveKey = activeView.parentKey ?? activeView.key;
  const navActiveView = gtccViewByKey(navActiveKey)!;
  // D-GTCC-FRAME1 (A6) — `ten` and `dplane` already run their own
  // page-internal `?tab=` UI (`TenView.tsx`, `DplaneView.tsx`), the same
  // mechanism `cockpit` uses above. Their `parentKey`-linked registry
  // siblings (`brand`/`approval`/`purge`/`supin`/`perm` under `ten`,
  // `dbops` under `dplane`) exist for deep-linking only; rendering the
  // nav-level `GtccViewTabs` bar for them on top of the view's own tabs
  // would duplicate it — the reference shows exactly one tab row here.
  const rawViewTabs = gtccChildrenOf(navActiveKey);
  const viewTabs = GTCC_INTERNAL_TAB_VIEWS.has(navActiveKey)
    ? []
    : GTCC_FOLDED_TAB_VIEWS.has(navActiveKey)
      ? rawViewTabs.filter((child) => child.component !== navActiveView.component)
      : rawViewTabs;
  const activeGroup = GTCC_GROUPS.find((group) => group.viewKeys.includes(navActiveKey));

  // D-GTCC-FRAME1 — some views (e.g. `GtccCockpitView`) keep their own tab
  // selection purely in `?tab=` and never register as `gtccChildrenOf`
  // (that mechanism is for Referenz-consolidated views only). The reference
  // breadcrumb still wants that page-internal tab as the 3rd segment, so
  // the Shell tracks the raw `tab` query param and resolves it against the
  // `admin.gtcc.<view>.tab.<id>` catalog convention those views already use
  // — `t(key) !== key` is the existence check (`translator()` falls back to
  // the raw key on a miss), so a view without that convention is unaffected.
  const [locationSearch, setLocationSearch] = useState(() =>
    typeof window === "undefined" ? "" : window.location.search,
  );
  // Layout effect, not a passive one: page-internal tab views (e.g.
  // `GtccCockpitView`) write their resolved default tab to `?tab=` from
  // their own mount effect. Passive effects fire bottom-up (children
  // before this ancestor), so a plain `useEffect` listener here would
  // still be unregistered when that write dispatches its event. Layout
  // effects across the whole tree run — and finish — before any passive
  // effect fires, so registering here is guaranteed to observe it.
  useLayoutEffect(() => {
    ensureGtccLocationChangeEvents();
    const onLocationChange = () => setLocationSearch(window.location.search);
    window.addEventListener("popstate", onLocationChange);
    window.addEventListener("gtcc:locationchange", onLocationChange);
    return () => {
      window.removeEventListener("popstate", onLocationChange);
      window.removeEventListener("gtcc:locationchange", onLocationChange);
    };
  }, []);
  const pageTabId = new URLSearchParams(locationSearch).get("tab");
  const pageTabKey = pageTabId ? `admin.gtcc.${activeView.key}.tab.${pageTabId}` : null;
  const pageTabLabel = pageTabKey && t(pageTabKey) !== pageTabKey ? pageTabKey : undefined;

  // I-GTCC-47: Pfad oder Legacy-Query beim Mount normalisieren und auf
  // Browser-Zurueck/Vorwaerts hoeren.
  useEffect(() => {
    // The gallery route is not a `GTCC_VIEWS` entry, so it must never be
    // rewritten to the unknown-key fallback (`ovw`) by the normal
    // path/query normalization.
    if (!isGtccUiGalleryLocation()) {
      writeGtccViewKeyToLocation(initialViewKeyFromLocation(), true, import.meta.env.BASE_URL);
    }
    const onPopState = () => setActiveKey(initialViewKeyFromLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const navCollapsed = useNavCollapsed();
  const [navOpen, setNavOpen] = useState(() => !isMaxWidthBreakpointNow());
  const [askOpen, setAskOpen] = useState(false);

  // D-GTCC-FRAME1 — sidebar counter badges (`alin` crit, `mail` warn) read
  // real counts from the same endpoints/query keys their own views use, so
  // the sidebar can never show a number the linked view disagrees with.
  // Both queries stay silent (no pill) on any non-`ok` result rather than
  // inventing a count for a disabled/forbidden/broken source.
  const alertsSummaryQuery = useQuery({
    queryKey: ALERTS_SUMMARY_KEY,
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchGtccAlertsSummary(signal),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
  const mailHealthQuery = useQuery({
    queryKey: MAIL_HEALTH_QUERY_KEY,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      apiGet<unknown>("/v1/admin/mail/health", undefined, signal),
    staleTime: 30_000,
  });
  const navPillOverrides: Partial<Record<string, GtccPill>> = {};
  const alertsSummary = alertsSummaryQuery.data;
  if (alertsSummary?.ok) {
    const activeCount = alertsSummary.data.alerts.filter((alert) => !alert.acknowledged).length;
    if (activeCount > 0) {
      navPillOverrides.alin = { text: String(activeCount), tone: "crit" };
    }
  }
  const mailHealthData = mailHealthQuery.data;
  if (isMailHealthResponse(mailHealthData)) {
    const warningCount = mailHealthWarningCount(mailHealthData);
    if (warningCount > 0) {
      navPillOverrides.mail = { text: String(warningCount), tone: "warn" };
    }
  }

  const navVisible = navOpen;

  const handleSelect = (key: string) => {
    setActiveKey(key);
    // Jeder Wechsel bekommt einen History-Eintrag im aktuellen URL-Schema.
    writeGtccViewKeyToLocation(key, false, import.meta.env.BASE_URL);
    if (navCollapsed) setNavOpen(false);
  };

  // B-GTCC-UI1 — hidden gallery route, resolved before any `GTCC_VIEWS`
  // lookup and rendered outside the normal shell chrome (no nav/topbar):
  // it is reachable only via `/gtcc/ui-gallery` or `?gtcc=ui-gallery`.
  if (activeKey === GTCC_UI_GALLERY_KEY) {
    return <GtccUiGalleryRoute />;
  }

  return (
    <div
      data-testid="gtcc-shell"
      data-surface="gtcc"
      data-mode={resolved === "light" ? "light" : undefined}
      style={{
        display: "flex",
        minHeight: "0",
        height: "100%",
        // C-GTCC-SHELL-1ZU1 — reference body background (`--bg`, black in dark
        // mode): the content region and the topbar sit on the same canvas, only
        // the hairline border between them separates the two.
        background: "var(--bg)",
        color: "var(--tx)",
        /* A-GTCC-ACCEPT-SHELL1 — reference
           `body{font-family:"Manrope",system-ui,sans-serif}`
           (GTCC_REFERENCE_2026-09-01.html:21). Manrope is the reference's
           default family for everything that does not name its own — the
           breadcrumb, the view prose, every unstyled label. The Cockpit
           inherited the admin app's base stack instead, which set the same
           text in narrower glyphs: on `cockpit-exec-dark`, where reference
           and actual read the identical crumb, the run measured 280 inked
           columns against 268, a 2.7% drift with an identical start x.
           `cockpit-fonts.css` self-hosts the family (@fontsource) and reaches
           every shell render through `GalleryView`, so the root is where the
           reference's base family belongs — the mono and display families
           stay on the elements that name them in `cockpit-ui.css`. */
        fontFamily: "Manrope, system-ui, sans-serif",
        /* A-GTCC-ACCEPT-SHELL1 — reference `body{line-height:1.55}`
           (GTCC_REFERENCE_2026-09-01.html:21). The app reset sets 1.5
           (packages/design-system/src/reset.css:25) and `button{font:inherit}`
           passes that on, so every menu row measured 38px instead of the
           reference's 38.6px and every group label 24px instead of 24.5px —
           a drift that accumulated to 25px by the bottom of the menu. The
           line-height ratio is part of the reference's typography, so it
           belongs on the Cockpit root, not on single elements. */
        lineHeight: 1.55,
        /* A-GTCC-ACCEPT-SHELL1 — reference `body{font-size:14px}` (same rule).
           The app-wide base is 15px (reset.css:26); every Cockpit element that
           does not set its own size — and that is most of the view content —
           inherited one step too large. The Cockpit root is where the
           reference establishes this base, so it is set here once instead of
           per element. */
        fontSize: "14px",
      }}
    >
      <h1
        data-testid="gtcc-shell-heading"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
        }}
      >
        {t("admin.gtcc.title")}
      </h1>
      <GtccSkipLink targetId={GTCC_MAIN_ID} />
      <div
        data-testid="gtcc-main-column"
        style={{
          display: "flex",
          // Keep the reference's sidebar at the visual start while preserving
          // Header -> Nav -> Main keyboard order in the DOM.
          order: 1,
          flex: 1,
          flexDirection: "column",
          minWidth: "0",
          minHeight: "0",
          /* C-GTCC-SHELL-1ZU1 — reference `.content` (GTCC_REFERENCE_2026-09-01.html:66):
             18px top, 22px inline, 30px bottom. The uniform 24px made the content
             start too high and hug the sidebar too closely. */
        }}
      >
        <GtccTopbar
          groupLabel={activeGroup?.label}
          viewLabel={navActiveView.label}
          tabLabel={
            viewTabs.length > 0
              ? activeView.parentKey
                ? (activeView.tabLabel ?? activeView.label)
                : activeView.label
              : pageTabLabel
          }
          navOpen={navVisible}
          onToggleNav={() => setNavOpen((open) => !open)}
          onOpenAskGenius={() => setAskOpen(true)}
        />
        <main
          id={GTCC_MAIN_ID}
          tabIndex={-1}
          data-testid="gtcc-content-frame"
          data-gtcc-active-view={activeView.key}
          style={{ flex: 1, overflow: "auto", minHeight: "0", padding: "18px 22px 30px" }}
        >
          {isOwner ? (
            <>
              {viewTabs.length > 0 && !GTCC_HEAD_TABS_VIEWS.has(navActiveKey) ? (
                <GtccViewTabs
                  parent={navActiveView}
                  tabs={viewTabs}
                  activeKey={activeView.key}
                  onSelect={handleSelect}
                />
              ) : null}
              {/* A-GTCC-VIEW-BOUNDARY1 — a render throw in a view stays in this slot;
                the key resets the boundary on every view switch, so the next view
                renders normally without a reload. A-GTCC-VIEW-BOUNDARY2 — der
                Retry raeumt zusaetzlich die Cache-Eintraege dieser View. */}
              <GtccViewBoundary key={activeView.key} queryPrefixes={activeView.queryPrefixes}>
                <GtccHeadTabsProvider
                  value={
                    viewTabs.length > 0 && GTCC_HEAD_TABS_VIEWS.has(navActiveKey) ? (
                      <GtccViewTabs
                        parent={navActiveView}
                        tabs={viewTabs}
                        activeKey={activeView.key}
                        onSelect={handleSelect}
                      />
                    ) : null
                  }
                >
                  <GtccActiveView view={activeView} />
                </GtccHeadTabsProvider>
              </GtccViewBoundary>
            </>
          ) : (
            <GtccNoAccessNotice />
          )}
        </main>
      </div>
      {navCollapsed ? (
        <Drawer
          open={navOpen}
          onClose={() => setNavOpen(false)}
          ariaLabel={t("admin.gtcc.nav_aria_label")}
          side="start"
        >
          <GtccNav
            activeKey={navActiveKey}
            onSelect={handleSelect}
            pillOverrides={navPillOverrides}
          />
        </Drawer>
      ) : (
        navVisible && (
          <GtccNav
            activeKey={navActiveKey}
            onSelect={handleSelect}
            pillOverrides={navPillOverrides}
          />
        )
      )}
      <Drawer
        open={askOpen}
        onClose={() => setAskOpen(false)}
        ariaLabel={t("admin.gtcc.ask_genius")}
        side="end"
        width="var(--gt-cmp-gtcc-panel-inline-size)"
      >
        <div style={{ padding: "var(--gt-sys-space-5, 24px)" }}>
          <AskGeniusPanel view={activeView} enabled={askOpen} />
        </div>
      </Drawer>
    </div>
  );
}

/**
 * B-GTCC-UI1 — sets the Cockpit's own closed 16-token namespace
 * (`[data-surface="gtcc"]`, see `packages/design-system/src/tokens.css`)
 * on a dedicated root so `cockpit-ui.css` applies, and mirrors the app's
 * resolved light/dark theme via `data-mode` (dark is the unmarked default).
 *
 * Carries its own theme toggle (same `data-testid` as the topbar's) since
 * this route renders without the shell topbar — `gtcc-shot.mjs` needs it
 * to reach the dark-mode screenshot.
 */
function GtccUiGalleryRoute() {
  const { resolved, setTheme } = useTheme();
  return (
    <div
      data-testid="gtcc-ui-gallery-route"
      data-surface="gtcc"
      data-mode={resolved === "light" ? "light" : undefined}
    >
      <button
        type="button"
        data-testid="gtcc-topbar-theme-toggle"
        onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
        style={topbarButtonStyle}
      >
        ◐
      </button>
      <GalleryView />
    </div>
  );
}

function GtccSkipLink({ targetId }: { readonly targetId: string }) {
  const { t } = useLocale();
  const [focused, setFocused] = useState(false);

  return (
    <a
      href={`#${targetId}`}
      data-testid="gtcc-skip-link"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={
        focused
          ? {
              position: "fixed",
              insetBlockStart: "var(--gt-sys-space-2, 8px)",
              insetInlineStart: "var(--gt-sys-space-2, 8px)",
              zIndex: "var(--gt-cmp-gtcc-topbar-z, 10)",
              padding: "var(--gt-sys-space-2, 8px) var(--gt-sys-space-3, 12px)",
              background: "var(--gt-sys-surface, #fff)",
              color: "var(--gt-sys-text, #111827)",
              border: "1px solid var(--gt-sys-border, #d1d5db)",
              borderRadius: "var(--gt-sys-radius-sm, 6px)",
            }
          : {
              position: "absolute",
              width: "1px",
              height: "1px",
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
            }
      }
    >
      {t("admin.gtcc.skip_link")}
    </a>
  );
}

interface GtccTopbarProps {
  readonly groupLabel: string | undefined;
  readonly viewLabel: string;
  readonly tabLabel: string | undefined;
  readonly navOpen: boolean;
  readonly onToggleNav: () => void;
  readonly onOpenAskGenius: () => void;
}

/**
 * Derives the env-chip label from the build-time GT_DEPLOY_ENV flag (baked into
 * the admin bundle via Vite's envPrefix, sourced from the same DEPLOY_ENV value
 * the API service already uses). Deliberately never falls back to "PROD" for an
 * unset/unrecognized value: a silent PROD default is a safety risk ahead of
 * destructive Danger-Zone actions on preview/test domains (GTCC audit G3h).
 */
function environmentLabel(deployEnv: string | undefined): "DEV" | "STAGING" | "PROD" | "UNKNOWN" {
  switch (deployEnv) {
    case "local":
      return "DEV";
    case "staging":
      return "STAGING";
    case "production":
      return "PROD";
    default:
      return "UNKNOWN";
  }
}

function GtccTopbar({
  groupLabel,
  viewLabel,
  tabLabel,
  navOpen,
  onToggleNav,
  onOpenAskGenius,
}: GtccTopbarProps) {
  const { t } = useLocale();
  const { resolved, setTheme } = useTheme();
  const envLabel = environmentLabel(import.meta.env.GT_DEPLOY_ENV as string | undefined);

  return (
    <header
      data-testid="gtcc-topbar"
      style={{
        display: "flex",
        alignItems: "center",
        /* C-GTCC-SHELL-1ZU1 — reference `header.top`
           (GTCC_REFERENCE_2026-09-01.html:57): same canvas as the content
           (`--bg2`), separated by a single hairline instead of a raised bar with
           a drop shadow. `gap:14px` and `padding:10px 20px` are the reference's. */
        gap: "14px",
        padding: "10px 20px",
        background: "var(--bg2)",
        borderBlockEnd: "1px solid var(--line)",
        zIndex: "var(--gt-cmp-gtcc-topbar-z, 10)",
      }}
    >
      <button
        type="button"
        aria-label={t("admin.gtcc.toggle_nav")}
        aria-expanded={navOpen}
        aria-controls="gtcc-nav"
        data-testid="gtcc-topbar-burger"
        onClick={onToggleNav}
        style={topbarButtonStyle}
      >
        ☰
      </button>
      {(() => {
        const resolvedView = t(viewLabel);
        const resolvedTab = tabLabel ? t(tabLabel) : undefined;
        return (
          <nav
            aria-label={t("admin.gtcc.location_aria_label")}
            data-testid="gtcc-topbar-breadcrumb"
            /* C-GTCC-SHELL-1ZU1 — reference `.crumb` / `.crumb b`
               (GTCC_REFERENCE_2026-09-01.html:59, markup :211): the whole trail
               is dimmed, only the leading group is bright and semibold. */
            style={{ fontSize: "13px", color: "var(--tx3)" }}
          >
            {groupLabel ? (
              <>
                <b style={{ color: "var(--tx)", fontWeight: 600 }}>{t(groupLabel)}</b>
                {" / "}
              </>
            ) : null}
            {resolvedView}
            {resolvedTab && resolvedTab !== resolvedView ? ` / ${resolvedTab}` : ""}
          </nav>
        );
      })()}
      <div
        style={{ marginInlineStart: "auto", display: "flex", alignItems: "center", gap: "10px" }}
      >
        <span data-testid="gtcc-topbar-env-chip" style={envChipStyle}>
          {envLabel}
        </span>
        <button
          type="button"
          data-testid="gtcc-topbar-ask-genius"
          onClick={onOpenAskGenius}
          style={askGeniusButtonStyle}
        >
          ✦ {t("admin.gtcc.ask_genius")}
        </button>
        <button
          type="button"
          aria-label={t("admin.gtcc.toggle_theme")}
          data-testid="gtcc-topbar-theme-toggle"
          onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
          style={topbarButtonStyle}
        >
          ◐
        </button>
      </div>
    </header>
  );
}

/**
 * C-GTCC-SHELL-1ZU1 — reference `.burger` / `.mode-btn`
 * (GTCC_REFERENCE_2026-09-01.html:58 and :64): both are the same bordered
 * 32x32 box. The old padding-sized button with a `--gt-sys-border` outline read
 * as no box at all against the black canvas, so the frame used `--line2`/`--tx2`
 * from the Cockpit's own token set instead.
 */
const topbarButtonStyle: CSSProperties = {
  border: "1px solid var(--line2)",
  borderRadius: "8px",
  background: "none",
  color: "var(--tx2)",
  inlineSize: "32px",
  blockSize: "32px",
  padding: 0,
  fontSize: "15px",
  lineHeight: 1,
  cursor: "pointer",
};

/**
 * D-GTCC-FRAME1 — matches the reference's `.ask` button: solid accent fill,
 * no border, white label, distinct from the bordered/transparent
 * burger/theme buttons that keep `topbarButtonStyle`.
 */
const askGeniusButtonStyle: CSSProperties = {
  border: "none",
  borderRadius: "8px",
  /* C-GTCC-SHELL-1ZU1 — the reference fills `.ask` with a gradient
     (GTCC_REFERENCE_2026-09-01.html:63), not a flat accent. */
  background: "linear-gradient(135deg, var(--accent), #2a8f9e)",
  color: "#fff",
  fontWeight: 700,
  fontSize: "13px",
  padding: "7px 14px",
  /* A-GTCC-ACCEPT-SHELL1 — the reference's `.ask` is 32px tall, exactly like
     the bordered burger/theme boxes next to it, so `header.top` closes at
     10+32+10 = 52px. In the reference document the button keeps the UA's
     `line-height: normal`; here the app reset hands buttons the inherited
     1.55, which grew the line box to 20px and the whole topbar to 54px. The
     fixed 32px reproduces the measured reference box independently of the
     font that happens to load. */
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  blockSize: "32px",
  cursor: "pointer",
};

/** C-GTCC-SHELL-1ZU1 — reference `.chip` / `.chip.env` (GTCC_REFERENCE_2026-09-01.html:61-62). */
const envChipStyle: CSSProperties = {
  fontFamily: '"JetBrains Mono", monospace',
  fontVariantNumeric: "tabular-nums",
  fontSize: "11px",
  border: "1px solid color-mix(in srgb, var(--warn) 45%, transparent)",
  borderRadius: "20px",
  color: "var(--warn)",
  padding: "3px 10px",
};

interface GtccViewTabsProps {
  readonly parent: GtccViewDef;
  readonly tabs: readonly GtccViewDef[];
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
}

/**
 * D-GTCC-SHELL1 — GTCC_1ZU1_PROGRAM.md P18/P19: ein konsolidierter Eltern-View
 * rendert sich selbst als erstes Tab, gefolgt von seinen `parentKey`-Kindern.
 * `role="tab"`/`aria-selected` statt `aria-current`, damit die Invariante
 * "genau ein `[aria-current=\"page\"]`" (Nav-Item) unangetastet bleibt.
 */
function GtccViewTabs({ parent, tabs, activeKey, onSelect }: GtccViewTabsProps) {
  const { t } = useLocale();
  const entries = [parent, ...tabs];
  return (
    <div
      role="tablist"
      aria-label={t("admin.gtcc.view_tabs_aria_label")}
      data-testid={`gtcc-tabs-${parent.key}`}
      /* C-GTCC-SHELL-1ZU1 — the tab strip sits on the content canvas, which is
         now the reference's `--bg`. Its rule and labels therefore read the
         Cockpit's own `--line`/`--tx*` like every other element on that canvas;
         `--gt-sys-border`/`--gt-sys-text` are the app-wide navy theme's values
         and looked foreign on black. */
      style={{
        display: "flex",
        gap: "var(--gt-sys-space-2, 8px)",
        borderBlockEnd: "1px solid var(--line, var(--gt-sys-border, #d1d5db))",
        marginBlockEnd: "var(--gt-sys-space-4, 16px)",
      }}
    >
      {entries.map((entry) => {
        const selected = entry.key === activeKey;
        return (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={`gtcc-tab-${entry.key}`}
            onClick={() => onSelect(entry.key)}
            style={{
              background: "none",
              border: "none",
              borderBlockEnd: selected
                ? "2px solid var(--accent, var(--gt-sys-accent, #2563eb))"
                : "2px solid transparent",
              padding: "var(--gt-sys-space-2, 8px) var(--gt-sys-space-1, 4px)",
              fontWeight: selected ? 600 : 400,
              cursor: "pointer",
              color: selected
                ? "var(--tx, var(--gt-sys-text, #111827))"
                : "var(--tx2, var(--gt-sys-text, #111827))",
            }}
          >
            {t(entry.parentKey ? (entry.tabLabel ?? entry.label) : entry.label)}
          </button>
        );
      })}
    </div>
  );
}

interface GtccNavProps {
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
  /** D-GTCC-FRAME1 — live counts keyed by view; falls back to `view.pill` when absent. */
  readonly pillOverrides: Partial<Record<string, GtccPill>>;
}

/** D-GTCC-SHELL1 item 2 — brand block, GT-Logo + Wordmark, reference-exact. */
function GtccBrand() {
  return (
    <div
      data-testid="gtcc-brand"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "16px 14px 14px",
        borderBottom: "1px solid var(--line, var(--gt-sys-border, #d1d5db))",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: "34px",
          height: "34px",
          borderRadius: "6px",
          background: "#0e2138",
          border: "1px solid #234b74",
          display: "grid",
          placeItems: "center",
          color: "#fff",
          fontFamily: "Sora, sans-serif",
          fontWeight: 700,
          fontSize: "15px",
          flex: "0 0 auto",
        }}
      >
        GT
      </div>
      <div
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontWeight: 700,
          fontSize: "11.5px",
          lineHeight: 1.3,
          letterSpacing: "0.01em",
        }}
      >
        GENIUS TRACKER
        <small
          style={{
            display: "block",
            fontFamily: "Manrope, sans-serif",
            fontWeight: 600,
            fontSize: "10px",
            color: "var(--tx3, var(--gt-sys-text-muted, #6b7280))",
            letterSpacing: "0.14em",
          }}
        >
          CONTROL COCKPIT
        </small>
      </div>
    </div>
  );
}

/** D-GTCC-SHELL1 item 2 — "Cockpit v<version> · <date>" from `/version`, honesty-rule-safe: no fabricated fallback text, just an empty footer until the fetch resolves. */
function GtccSideFoot() {
  const [info, setInfo] = useState<{ version: string; date: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGet<{ version?: string; buildTime?: string | null; startedAt?: string }>("/version")
      .then((res) => {
        if (cancelled) return;
        const date = (res.buildTime ?? res.startedAt ?? "").slice(0, 10);
        if (res.version && date) setInfo({ version: res.version, date });
      })
      .catch(() => {
        /* honesty rule: no data beats invented data — foot stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info) return null;

  return (
    <div
      data-testid="gtcc-side-foot"
      style={{
        borderTop: "1px solid var(--line, var(--gt-sys-border, #d1d5db))",
        padding: "10px 14px",
        fontSize: "11px",
        color: "var(--tx3, var(--gt-sys-text-muted, #6b7280))",
      }}
    >
      Cockpit v{info.version} · {info.date}
    </div>
  );
}

function GtccNav({ activeKey, onSelect, pillOverrides }: GtccNavProps) {
  const { t } = useLocale();
  return (
    <nav
      id="gtcc-nav"
      aria-label={t("admin.gtcc.nav_aria_label")}
      data-testid="gtcc-nav"
      style={{
        display: "flex",
        inlineSize: "var(--gt-cmp-gtcc-nav-inline-size, 248px)",
        flex: "0 0 auto",
        flexDirection: "column",
        /* C-GTCC-SHELL-1ZU1 — reference `.side`
           (GTCC_REFERENCE_2026-09-01.html:28): the sidebar shares the content's
           near-black canvas (`--bg2`) and is separated only by a hairline.
           The removed nav-surface component token resolved to the app-wide dark
           navy surface and made the sidebar a separate blue panel. */
        background: "var(--bg2)",
        borderInlineEnd: "1px solid var(--line)",
        zIndex: "var(--gt-cmp-gtcc-nav-z, 5)",
      }}
    >
      <GtccBrand />
      <div
        style={{
          flex: 1,
          /* A-GTCC-ACCEPT-SHELL1 — the reference stacks the groups in normal
             block flow (`.grp{margin-top:16px}`), where the last item's 1px
             bottom margin collapses into the next group's 16px. A flex column
             with a `gap` cannot collapse margins, so the same numbers would
             render 17px. Block flow reproduces the reference exactly; the
             `overflow:auto` is the reference's own `nav{overflow-y:auto}` and
             keeps the first group's margin from collapsing out of the top. */
          overflow: "auto",
          /* A-GTCC-ACCEPT-SHELL1 — reference `nav` (GTCC_REFERENCE_2026-09-01.html:33):
             `padding:10px 0 14px`, not the symmetric 8px. Together with the
             first group's own 8px (`.grp:first-child`, :34) the first group
             label starts 18px below the brand hairline; the 8px padding alone
             pulled the whole menu 10px up against the reference. */
          padding: "10px 0 14px",
        }}
      >
        {GTCC_GROUPS.map((group, groupIndex) => (
          <div
            key={group.id}
            data-testid={`gtcc-nav-group-${group.id}`}
            role="group"
            aria-labelledby={`gtcc-nav-group-${group.id}-heading`}
            /* A-GTCC-ACCEPT-SHELL1 — reference `.grp{margin-top:16px}` and
               `.grp:first-child{margin-top:8px}`
               (GTCC_REFERENCE_2026-09-01.html:34). The previous flex `gap` read
               `--gt-cmp-gtcc-nav-group-gap`, which resolves to the app-wide
               `--gt-sys-space-5` (24px) and pushed every group boundary 9px
               apart from the reference. */
            style={{ marginBlockStart: groupIndex === 0 ? "8px" : "16px" }}
          >
            <div
              id={`gtcc-nav-group-${group.id}-heading`}
              /* C-GTCC-SHELL-1ZU1 — reference `.grp-label`
                 (GTCC_REFERENCE_2026-09-01.html:37): 10px, semibold, wide
                 tracking, and a 7px gap down to the first item. */
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.16em",
                padding: "2px var(--gt-cmp-gtcc-nav-item-inline-padding, 16px) 7px",
                color: "var(--tx3)",
              }}
            >
              <span
                aria-hidden="true"
                data-testid={`gtcc-nav-group-${group.id}-no`}
                style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: "9px",
                  color: "#04182e",
                  background: "var(--accent, var(--gt-sys-accent, #2563eb))",
                  borderRadius: "3px",
                  padding: "0 4px",
                  fontWeight: 600,
                }}
              >
                {`M${groupIndex + 1}`}
              </span>
              <span>{t(group.label)}</span>
            </div>
            {group.viewKeys.map((key) => {
              const view = GTCC_VIEWS.find((v) => v.key === key);
              if (!view) return null;
              const isActive = view.key === activeKey;
              const pill = pillOverrides[view.key] ?? view.pill;
              return (
                <button
                  key={view.key}
                  type="button"
                  data-testid={`gtcc-nav-item-${view.key}`}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => onSelect(view.key)}
                  /* C-GTCC-SHELL-1ZU1 — reference `.it` / `.it.active`
                     (GTCC_REFERENCE_2026-09-01.html:40 and :42): the item is
                     inset inside the sidebar (`margin:1px 8px`) with its own
                     rounded corners, not a full-bleed row, and its resting text
                     is the dimmed `--tx2`. `minBlockSize` keeps the touch floor
                     (`--gt-cmp-gtcc-nav-item-block-size`, 44px under
                     `pointer: coarse`) without fixing the fine-pointer row
                     height above the reference's padding-derived one. */
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    width: "calc(100% - 16px)",
                    margin: "1px 8px",
                    textAlign: "left",
                    fontFamily: '"JetBrains Mono", monospace',
                    /* A-GTCC-ACCEPT-SHELL1 — reference `.it{font-size:12px}`
                       (GTCC_REFERENCE_2026-09-01.html:38). `--gt-sys-font-size-sm`
                       is the app-wide 13px, which made every menu row 40px
                       instead of the reference's 38px and shifted the whole
                       menu down. The reference value is literal here, like the
                       `padding:9px 15px` next to it. */
                    fontSize: "12px",
                    fontWeight: isActive ? 500 : 400,
                    letterSpacing: 0,
                    whiteSpace: "nowrap",
                    background: isActive
                      ? "color-mix(in oklab, var(--accent, var(--gt-sys-accent, #2563eb)) 12%, transparent)"
                      : "transparent",
                    border: isActive
                      ? "1px dashed color-mix(in oklab, var(--accent, var(--gt-sys-accent, #2563eb)) 55%, transparent)"
                      : "1px solid transparent",
                    borderRadius: "8px",
                    minBlockSize: "var(--gt-cmp-gtcc-nav-item-block-size, 32px)",
                    padding: "9px 15px",
                    color: view.dangerNavMark
                      ? "var(--crit, var(--gt-sys-danger, #b91c1c))"
                      : isActive
                        ? /* C-GTCC-SHELL-1ZU1 — the reference writes the active
                             label as the literal `#eaf3ff`
                             (GTCC_REFERENCE_2026-09-01.html:42). That value is the
                             Cockpit's bright text in the dark set only; taken
                             literally it turns near-white on the pale accent tint
                             of the light mode. The Cockpit's own `--tx` is that
                             same bright value in dark and flips in light. */
                          "var(--tx, var(--gt-sys-text, #111827))"
                        : "var(--tx2, var(--gt-sys-text, #111827))",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {isGtccIconKey(view.key) ? (
                      <span
                        aria-hidden="true"
                        style={{
                          width: "18px",
                          flex: "0 0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          /* C-GTCC-SHELL-1ZU1 — reference `.ic`, `.it.active .ic`
                             and `.danger .ic`
                             (GTCC_REFERENCE_2026-09-01.html:41-43, :48). */
                          color: view.dangerNavMark
                            ? "var(--crit, var(--gt-sys-danger, #b91c1c))"
                            : isActive
                              ? "var(--accent, var(--gt-sys-accent, #2563eb))"
                              : "var(--tx3, var(--gt-sys-text-muted, #6b7280))",
                        }}
                      >
                        <GtccIcon iconKey={view.key} />
                      </span>
                    ) : null}
                    <span>{t(view.label)}</span>
                    {view.danger ? (
                      <span style={GTCC_VISUALLY_HIDDEN}>
                        {` (${t("admin.gtcc.danger_zone_marker")})`}
                      </span>
                    ) : null}
                  </span>
                  {pill ? (
                    <span
                      /* C-GTCC-SHELL-1ZU1 — reference `.pill` / `.pill.crit` /
                         `.pill.warn` (GTCC_REFERENCE_2026-09-01.html:45-47):
                         10px semibold mono in a 3px-radius chip whose tint is
                         the Cockpit's own `--crit`/`--warn` over the sidebar,
                         not the pill-shaped app-wide badge. */
                      style={{
                        marginInlineStart: "auto",
                        fontFamily: '"JetBrains Mono", monospace',
                        fontVariantNumeric: "tabular-nums",
                        fontSize: "10px",
                        fontWeight: 600,
                        borderRadius: "3px",
                        padding: "1px 7px",
                        background:
                          pill.tone === "crit"
                            ? "color-mix(in oklab, var(--crit, var(--gt-sys-danger, #b91c1c)) 18%, transparent)"
                            : "color-mix(in oklab, var(--warn, var(--gt-sys-warning, #92400e)) 16%, transparent)",
                        color:
                          pill.tone === "crit"
                            ? "var(--crit, var(--gt-sys-danger, #b91c1c))"
                            : "var(--warn, var(--gt-sys-warning, #92400e))",
                      }}
                    >
                      {pill.text}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <GtccSideFoot />
    </nav>
  );
}
